import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TrashIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { api, ApiError } from "../lib/api";
import { useToast } from "../components/Toast";
import { SignedAmount } from "../components/SignedAmount";
import { formatDate, formatDateTime } from "../lib/format";
import { useBuchungsarten, type Buchungsart } from "../lib/receipts";
import { devonthinkLink } from "../../lib/devonthink_link.ts";

/**
 * Buchungen (Block 3b, Ziel „Ergebnisgleichheit“, 18.10.2026). Steuerwirkung entsteht nur über bestätigte Buchungen.
 * Status: grün = bestätigt (erledigt), blau = alles, was noch Arbeit braucht (offen, Vorschlag, zugeordnet, Konflikt).
 * Beträge: blau = Einnahme, rot = Ausgabe. Die Seite startet immer mit einem Steuerjahr (nie alle Buchungen auf einmal).
 */
type Status = "bestaetigt" | "geaendert" | "konflikt" | "offen" | "vorschlag" | "zugeordnet";
type Teil = { steuerArtId: string | null; betragCent: number; merkmal: string | null; herkunft: string; receiptId?: string;
  nutzung?: "vermietet" | "arbeitszimmer" | "privat" | null; aufteilung?: { nutzung: string | null; betragCent: number }[] };
type Buchung = { id: string; konto_id: string; datum: string; betrag_cent: number; text: string | null; gegenpartei: string | null; steuerjahr: number;
  steuerjahr_bestaetigt: number; bestaetigt_am: number | null; quelle: string; leistung_von?: string | null; leistung_bis?: string | null; teile: Teil[]; status: Status; vorschlagArtId: string | null; belege: number; dokumente: number; steuerrelevant: boolean;
  jahreswechsel: boolean; flaechen?: Flaeche[] };
type Konto = { id: string; name: string };
/** Berechnete Flächen-Aufteilung (nur Anzeige): so verteilt die Steuerrechnung Hauskosten/Kreditzinsen — nicht gespeichert. */
type Flaeche = { steuerArtId: string; betragCent: number; art: "wohnkosten" | "garten" | "kredit"; vermietetCent: number; azCent: number; privatCent: number;
  azWeg: "lexoffice" | "euer172" | "zaehlt_nicht"; hinweis?: string };
const AZ_WEG: Record<Flaeche["azWeg"], string> = { lexoffice: "in Lexoffice gebucht", euer172: "über Anlage EÜR Zeile 172", zaehlt_nicht: "zählt nicht einzeln (Pauschale bzw. kein Mittelpunkt)" };
/** Kurzform für die Liste: „Strom → vermietet 43,03 € · Arbeitszimmer 16,39 € · privat 60,58 €“ (Anteile 0 entfallen). */
function flaecheKurz(f: Flaeche, artName: Map<string, string>): string {
  if (f.hinweis) return `${artName.get(f.steuerArtId) ?? "?"}: ${f.hinweis}`;
  const teile = [["vermietet", f.vermietetCent], ["Arbeitszimmer", f.azCent], ["privat", f.privatCent]].filter(([, c]) => c !== 0)
    .map(([n, c]) => `${n} ${euro(c as number)}`);
  return `${artName.get(f.steuerArtId) ?? "?"} → ${teile.join(" · ")}`;
}
type Aenderung = { feld: string; alt: string | null; neu: string | null; zeit: number; username: string | null };

const STATUS_TEXT: Record<Status, string> = { bestaetigt: "bestätigt", geaendert: "geändert – neu prüfen", konflikt: "Konflikt", offen: "offen", vorschlag: "Vorschlag", zugeordnet: "zugeordnet" };
type Filter = Status | "ohneBeleg" | "steuerOhneBeleg";
const FILTER: { wert: Filter | ""; text: string }[] = [
  { wert: "", text: "Alle" }, { wert: "offen", text: "Offen" }, { wert: "vorschlag", text: "Vorschlag" }, { wert: "zugeordnet", text: "Zugeordnet" },
  { wert: "konflikt", text: "Konflikt" }, { wert: "geaendert", text: "Geändert" }, { wert: "bestaetigt", text: "Bestätigt" }, { wert: "ohneBeleg", text: "Ohne Beleg" },
  { wert: "steuerOhneBeleg", text: "Steuerrelevant ohne Beleg" },
];
const SEITE = 200;
const feld = "border border-ios-line rounded-lg px-2 py-1.5 text-[16px] lg:text-[14px] bg-white";
const fehlertext = (e: unknown) => (e instanceof ApiError ? e.message : "Aktion fehlgeschlagen");
const euro = (c: number) => (c / 100).toFixed(2).replace(".", ",");
const parseEuroCent = (s: string) => { const n = Number(s.trim().replace(/\./g, "").replace(",", ".")); return Number.isFinite(n) ? Math.round(n * 100) : NaN; };

function StatusPille({ s }: { s: Status }) {
  return <span className={clsx("text-[12px] px-2 py-0.5 rounded-full whitespace-nowrap", s === "bestaetigt" ? "bg-ios-green/15 text-ios-green" : "bg-ios-blue/10 text-ios-blue")}>
    {STATUS_TEXT[s]}</span>;
}

export function BuchungenPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [sp, setSp] = useSearchParams();
  const { data: jahre } = useQuery<{ jahr: number; anzahl: number }[]>({ queryKey: ["buchungen-jahre"], queryFn: () => api.get("/api/buchungen/jahre") });
  const { data: konten } = useQuery<Konto[]>({ queryKey: ["konten"], queryFn: () => api.get("/api/konten") });
  const jahr = Number(sp.get("jahr")) || jahre?.[0]?.jahr || null;
  const kontoId = sp.get("konto") ?? "";
  const status = (sp.get("status") ?? "") as Filter | "";
  const setze = (k: string, v: string) => setSp((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });

  const q = new URLSearchParams({ ...(jahr ? { steuerjahr: String(jahr) } : {}), ...(kontoId ? { kontoId } : {}) }).toString();
  const { data: alle, isLoading } = useQuery<Buchung[]>({ queryKey: ["buchungen", q], queryFn: () => api.get(`/api/buchungen?${q}`), enabled: jahr !== null });
  const { data: artDaten } = useBuchungsarten(jahr ?? undefined);
  const arten = artDaten?.arten ?? [];
  const artName = useMemo(() => new Map(arten.map((a) => [a.id, a.name])), [arten]);
  const kontoName = useMemo(() => new Map((konten ?? []).map((k) => [k.id, k.name])), [konten]);

  const zaehler = useMemo(() => {
    const z = new Map<string, number>();
    for (const b of alle ?? []) { z.set(b.status, (z.get(b.status) ?? 0) + 1); if (b.belege === 0 && b.dokumente === 0) { z.set("ohneBeleg", (z.get("ohneBeleg") ?? 0) + 1); if (b.steuerrelevant) z.set("steuerOhneBeleg", (z.get("steuerOhneBeleg") ?? 0) + 1); } }
    return z;
  }, [alle]);
  const liste = useMemo(() => (alle ?? []).filter((b) => (status === "ohneBeleg" ? b.belege === 0 && b.dokumente === 0
    : status === "steuerOhneBeleg" ? b.belege === 0 && b.dokumente === 0 && b.steuerrelevant : !status || b.status === status)), [alle, status]);
  const [anzahl, setAnzahl] = useState(SEITE);
  useEffect(() => setAnzahl(SEITE), [q, status]);
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [offenId, setOffenId] = useState<string | null>(null);
  useEffect(() => { setAuswahl(new Set()); setOffenId(null); }, [q, status]);

  const neuLaden = () => { void qc.invalidateQueries({ queryKey: ["buchungen"] }); void qc.invalidateQueries({ queryKey: ["buchungen-jahre"] }); };
  const bestaetigen = useMutation({
    mutationFn: (ids: string[]) => api.post<{ bestaetigt: number; uebersprungen: { id: string; grund: string }[] }>("/api/buchungen/bestaetigen", { ids }),
    onSuccess: (r) => {
      setAuswahl(new Set());
      neuLaden();
      if (r.uebersprungen.length === 0) toast.success(`${r.bestaetigt} bestätigt`);
      else {
        const gruende = new Map<string, number>();
        for (const u of r.uebersprungen) gruende.set(u.grund, (gruende.get(u.grund) ?? 0) + 1);
        toast.warn(`${r.bestaetigt} bestätigt, ${r.uebersprungen.length} übersprungen: ${[...gruende].map(([g, n]) => `${n}× ${g}`).join("; ")}`);
      }
    },
    onError: (e) => toast.error(fehlertext(e)),
  });
  const bereit = liste.filter((b) => b.status === "vorschlag" || b.status === "zugeordnet").map((b) => b.id);
  // Modell B: nach einer Einstellungsänderung (Fläche, Kreditplan) gesammelt neu bestätigen; abgeschlossenes Jahr → Rückfrage (zentral)
  const geaendert = liste.filter((b) => b.status === "geaendert").map((b) => b.id);

  return (
    <div className="max-w-5xl mx-auto p-4 lg:p-6">
      <div className="flex items-baseline gap-3 mb-1">
        <h1 className="text-[22px] font-semibold tracking-tight flex-1">Buchungen</h1>
        <Link to="/steuer" className="text-[14px] text-ios-blue">Steuer →</Link>
        <Link to="/buchungen/abgleich" className="text-[14px] text-ios-blue">Abgleich mit Belegen →</Link>
      </div>
      <p className="text-[13px] text-ios-muted mb-3">
        Nur bestätigte Buchungen zählen in der Steuer. Ein geprüfter Beleg liefert die Aufteilung, der Betrag kommt immer von der Buchung.
      </p>
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <select className={feld} value={jahr ?? ""} onChange={(e) => setze("jahr", e.target.value)} aria-label="Steuerjahr">
          {(jahre ?? []).map((j) => <option key={j.jahr} value={j.jahr}>Steuerjahr {j.jahr} ({j.anzahl})</option>)}
        </select>
        <select className={feld} value={kontoId} onChange={(e) => setze("konto", e.target.value)} aria-label="Konto">
          <option value="">alle Konten</option>
          {(konten ?? []).map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {FILTER.map((f) => {
          const n = f.wert ? zaehler.get(f.wert) ?? 0 : (alle ?? []).length;
          return (
            <button key={f.wert} type="button" onClick={() => setze("status", f.wert)}
              className={clsx("text-[13px] px-3 py-1 rounded-full", status === f.wert ? "bg-ios-blue text-white" : "bg-white text-ios-ink shadow-card")}>
              {f.text} <span className="opacity-70">{n}</span>
            </button>
          );
        })}
      </div>
      {jahre && jahre.length === 0 && <p className="text-[14px] text-ios-muted">Noch keine Buchungen. Kontoauszüge importierst du unter Einstellungen › Konten.</p>}
      {isLoading && <p className="text-[14px] text-ios-muted">Lädt …</p>}
      {liste.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center mb-2 text-[13px]">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={auswahl.size > 0 && auswahl.size === Math.min(anzahl, liste.length)}
              onChange={(e) => setAuswahl(e.target.checked ? new Set(liste.slice(0, anzahl).map((b) => b.id)) : new Set())} /> sichtbare auswählen
          </label>
          <button type="button" disabled={auswahl.size === 0 || bestaetigen.isPending} onClick={() => bestaetigen.mutate([...auswahl])}
            className="px-3 py-1 rounded-ios bg-ios-blue text-white disabled:opacity-40">{auswahl.size} ausgewählte bestätigen</button>
          <button type="button" disabled={bereit.length === 0 || bestaetigen.isPending} onClick={() => bestaetigen.mutate(bereit)}
            className="px-3 py-1 rounded-ios bg-white shadow-card text-ios-blue disabled:opacity-40">alle Vorschläge und Zugeordneten bestätigen ({bereit.length})</button>
          {geaendert.length > 0 && <button type="button" disabled={bestaetigen.isPending} onClick={() => bestaetigen.mutate(geaendert)}
            className="px-3 py-1 rounded-ios bg-white shadow-card text-ios-blue disabled:opacity-40">alle geänderten neu bestätigen ({geaendert.length})</button>}
        </div>
      )}
      <div className="bg-white rounded-iosLg shadow-card divide-y divide-ios-line">
        {liste.slice(0, anzahl).map((b) => (
          <div key={b.id}>
            <div className="flex items-start gap-2 px-3 py-2">
              <input type="checkbox" className="mt-1" checked={auswahl.has(b.id)} aria-label="auswählen"
                onChange={(e) => { const n = new Set(auswahl); if (e.target.checked) n.add(b.id); else n.delete(b.id); setAuswahl(n); }} />
              <button type="button" className="flex-1 min-w-0 text-left" onClick={() => setOffenId(offenId === b.id ? null : b.id)}>
                <div className="flex items-baseline gap-2">
                  <span className="text-[13px] text-ios-muted tabular-nums w-20 shrink-0">{formatDate(b.datum)}</span>
                  <span className="text-[14px] truncate flex-1">{b.gegenpartei || b.text || "—"}</span>
                  <SignedAmount value={b.betrag_cent / 100} treatNegativeAsIncome={false} className="text-[14px] shrink-0" />
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[12px] text-ios-muted w-20 shrink-0 truncate">{kontoName.get(b.konto_id) ?? ""}</span>
                  <span className="text-[12px] text-ios-dim truncate flex-1">
                    {b.status === "vorschlag" ? `Vorschlag: ${artName.get(b.vorschlagArtId!) ?? "?"}`
                      : b.teile.map((t) => (t.steuerArtId ? artName.get(t.steuerArtId) ?? "?" : t.herkunft === "konflikt" ? "Konflikt" : "offen")
                        + (b.teile.length > 1 ? ` ${euro(t.betragCent)}` : "")).join(" · ")}
                    {b.belege > 0 && <span className="ml-1 text-ios-muted">· {b.belege} Beleg{b.belege > 1 ? "e" : ""}</span>}
                    {b.dokumente > 0 && <span className="ml-1 text-ios-muted">· {b.dokumente} Dokument{b.dokumente > 1 ? "e" : ""}</span>}
                  </span>
                  <StatusPille s={b.status} />
                </div>
                {(b.flaechen ?? []).map((f, i) => (
                  <div key={i} className="text-[12px] text-ios-muted truncate pl-[5.5rem]" title="nach Fläche berechnet (Steuerrechnung), nicht gespeichert">
                    ↳ {flaecheKurz(f, artName)} <span className="italic">· nach Fläche berechnet</span>
                  </div>
                ))}
              </button>
            </div>
            {offenId === b.id && <BuchungDetail b={b} arten={arten} onGeaendert={neuLaden} />}
          </div>
        ))}
      </div>
      {liste.length > anzahl && (
        <button type="button" className="mt-3 text-[14px] text-ios-blue" onClick={() => setAnzahl(anzahl + SEITE)}>
          weitere {Math.min(SEITE, liste.length - anzahl)} von {liste.length - anzahl} anzeigen
        </button>
      )}
    </div>
  );
}

function BuchungDetail({ b, arten, onGeaendert }: { b: Buchung; arten: Buchungsart[]; onGeaendert: () => void }) {
  const toast = useToast();
  const eigene = b.teile.filter((t) => t.herkunft !== "beleg" && t.herkunft !== "konflikt" && !(t.herkunft === "offen"));
  const ausBeleg = b.teile.filter((t) => t.herkunft === "beleg");
  const ziel = b.betrag_cent - ausBeleg.reduce((a, t) => a + t.betragCent, 0);
  const start = eigene.length > 0 ? eigene : [{ steuerArtId: b.vorschlagArtId, betragCent: ziel }];
  // nutzung = ausdrückliche Vorgabe eines eigenen Teils (Modell B) — beim Speichern unverändert mitgeben, sonst ginge sie verloren
  const [teile, setTeile] = useState(start.map((t) => ({ steuerArtId: t.steuerArtId ?? "", betrag: euro(t.betragCent), nutzung: ("nutzung" in t ? t.nutzung : null) ?? null })));
  const [jahr, setJahr] = useState(String(b.steuerjahr));
  const qc = useQueryClient();
  const { data: belege } = useQuery<{ id: string; vendor: string | null; gesamt: number; reviewed_at: number | null }[]>({
    queryKey: ["buchung-belege", b.id], queryFn: () => api.get(`/api/buchungen/${b.id}/belege`) });
  const { data: dokumente } = useQuery<{ id: string; name: string; mime: string; groesse: number; dtUuid: string | null }[]>({
    queryKey: ["buchung-dokumente", b.id], queryFn: () => api.get(`/api/buchungen/${b.id}/dokumente`), enabled: b.dokumente > 0 });
  const loesen = useMutation({
    mutationFn: (receiptId: string) => api.post("/api/abgleich/loesen", { buchungId: b.id, receiptId }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["buchung-belege", b.id] }); void qc.invalidateQueries({ queryKey: ["abgleich"] }); fertig("Verknüpfung gelöst"); },
    onError: (e) => toast.error(fehlertext(e)),
  });
  const { data: protokoll } = useQuery<Aenderung[]>({ queryKey: ["buchung-aenderungen", b.id], queryFn: () => api.get(`/api/buchungen/${b.id}/aenderungen`) });
  const summe = teile.reduce((a, t) => a + (parseEuroCent(t.betrag) || 0), 0);
  const konflikt = b.status === "konflikt";
  const fertig = (msg: string) => { toast.success(msg); onGeaendert(); };
  const speichern = useMutation({
    mutationFn: () => api.put(`/api/buchungen/${b.id}/teile`, { teile: teile.map((t) => ({ steuerArtId: t.steuerArtId || null, betragCent: parseEuroCent(t.betrag), nutzung: t.nutzung })) }),
    onSuccess: () => fertig("Aufteilung gespeichert — bitte bestätigen"), onError: (e) => toast.error(fehlertext(e)),
  });
  // Leistungszeitraum (Modell B): für welchen Zeitraum gezahlt wurde — leer = Monat der Buchung; beide Daten oder keins
  const [lz, setLz] = useState({ von: b.leistung_von ?? "", bis: b.leistung_bis ?? "" });
  const lzSpeichern = useMutation({
    mutationFn: () => api.put(`/api/buchungen/${b.id}/leistungszeitraum`, { von: lz.von || null, bis: lz.bis || null }),
    onSuccess: () => fertig("Leistungszeitraum gespeichert"), onError: (e) => toast.error(fehlertext(e)),
  });
  const jahrSpeichern = useMutation({
    mutationFn: () => api.put(`/api/buchungen/${b.id}/steuerjahr`, { jahr: Number(jahr) }),
    onSuccess: () => fertig("Steuerjahr gespeichert"), onError: (e) => toast.error(fehlertext(e)),
  });
  const bestaetigen = useMutation({
    mutationFn: () => api.post<{ bestaetigt: number; uebersprungen: { grund: string }[] }>("/api/buchungen/bestaetigen", { ids: [b.id] }),
    onSuccess: (r) => (r.bestaetigt ? fertig("Bestätigt") : toast.warn(r.uebersprungen[0]?.grund ?? "nicht bestätigt")), onError: (e) => toast.error(fehlertext(e)),
  });
  const aufheben = useMutation({
    mutationFn: () => api.post(`/api/buchungen/${b.id}/bestaetigung-aufheben`), onSuccess: () => fertig("Bestätigung aufgehoben"), onError: (e) => toast.error(fehlertext(e)),
  });

  return (
    <div className="bg-ios-bg/60 px-4 py-3 space-y-3 text-[14px]">
      {b.text && <div className="text-[13px] text-ios-dim break-words">{b.text}</div>}
      {(b.flaechen ?? []).length > 0 && (
        <div>
          <div className="text-[13px] text-ios-muted mb-1">Für die Steuer nach Fläche verteilt (berechnet {b.steuerjahr}, nicht gespeichert — ändert sich mit den Flächen in den Jahreswerten)</div>
          {b.flaechen!.map((f, i) => (
            <div key={i} className="mb-1">
              <div className="flex justify-between gap-3"><span>{artName(arten, f.steuerArtId)}</span><SignedAmount value={f.betragCent / 100} treatNegativeAsIncome={false} /></div>
              {f.hinweis ? <div className="text-[13px] text-ios-blue pl-3">{f.hinweis}</div> : <>
                {f.vermietetCent !== 0 && <div className="flex justify-between gap-3 pl-3 text-[13px]"><span>§ 21 vermietet</span><SignedAmount value={f.vermietetCent / 100} treatNegativeAsIncome={false} /></div>}
                {f.azCent !== 0 && <div className="flex justify-between gap-3 pl-3 text-[13px]"><span>Arbeitszimmer <span className="text-ios-muted">({AZ_WEG[f.azWeg]})</span></span><SignedAmount value={f.azCent / 100} treatNegativeAsIncome={false} /></div>}
                {f.privatCent !== 0 && <div className="flex justify-between gap-3 pl-3 text-[13px]"><span>privat</span><SignedAmount value={f.privatCent / 100} treatNegativeAsIncome={false} /></div>}
              </>}
            </div>
          ))}
        </div>
      )}
      {b.status === "geaendert" && <p className="text-ios-blue">Seit dem Bestätigen hat sich etwas geändert (verknüpfter Beleg, Fläche, Kreditplan oder
        Leistungszeitraum). Die Steuerrechnung zählt noch den bestätigten Stand — bitte prüfen und neu bestätigen.</p>}
      {dokumente && dokumente.length > 0 && (
        <div>
          <div className="text-[13px] text-ios-muted mb-1">Dokumente (Kopie aus DEVONthink)</div>
          {dokumente.map((d) => (
            <div key={d.id} className="flex items-center gap-3">
              <a href={`/api/dokumente/${d.id}/datei`} target="_blank" rel="noreferrer" className="flex-1 text-ios-blue truncate">{d.name}</a>
              <a href={`/api/dokumente/${d.id}/datei?download=1`} className="text-[13px] text-ios-blue shrink-0">herunterladen</a>
              {devonthinkLink(d.dtUuid) && <a href={devonthinkLink(d.dtUuid)!} className="text-[13px] text-ios-blue shrink-0"
                title="Öffnet das Original in DEVONthink (Mac). Die Kopie hier bleibt, auch wenn das Original verschoben oder gelöscht wird.">in DEVONthink öffnen (Mac)</a>}
            </div>
          ))}
        </div>
      )}
      {belege && belege.length > 0 && (
        <div>
          <div className="text-[13px] text-ios-muted mb-1">verknüpfte Belege</div>
          {belege.map((r) => (
            <div key={r.id} className="flex items-center gap-2">
              <Link to={`/receipts/${r.id}`} className="flex-1 text-ios-blue truncate">{r.vendor ?? "Beleg"}{r.reviewed_at ? "" : " (noch nicht geprüft — zählt erst nach Prüfung)"}</Link>
              <SignedAmount value={-r.gesamt} treatNegativeAsIncome={false} />
              <button type="button" className="text-[13px] text-ios-dim" disabled={loesen.isPending} onClick={() => loesen.mutate(r.id)}>lösen</button>
            </div>
          ))}
        </div>
      )}
      {ausBeleg.length > 0 && (
        <div>
          <div className="text-[13px] text-ios-muted mb-1">aus geprüftem Beleg</div>
          {ausBeleg.map((t, i) => <div key={i} className="flex justify-between"><span>{arten.find((a) => a.id === t.steuerArtId)?.name ?? "ohne Art"}</span>
            <SignedAmount value={t.betragCent / 100} treatNegativeAsIncome={false} /></div>)}
        </div>
      )}
      {!konflikt && ausBeleg.length > 0 && ziel === 0 && eigene.length === 0 ? <p className="text-[13px] text-ios-muted">Der Beleg erklärt den ganzen Betrag.</p>
        : konflikt ? <p className="text-ios-blue">Konflikt: Die verknüpften Belege erklären mehr als die Buchung oder haben das andere Vorzeichen. Bitte erst die Verknüpfung klären.</p> : (
        <div>
          <div className="text-[13px] text-ios-muted mb-1">{ausBeleg.length ? "Rest zuordnen" : "Aufteilung"} — Summe muss {euro(ziel)} € sein</div>
          {teile.map((t, i) => (
            <div key={i} className="flex gap-2 mb-2 items-center">
              <select className={`${feld} flex-1 min-w-0`} value={t.steuerArtId} onChange={(e) => setTeile(teile.map((x, k) => (k === i ? { ...x, steuerArtId: e.target.value } : x)))}>
                <option value="">— offen lassen —</option>
                {arten.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <input className={`${feld} w-28 text-right`} inputMode="decimal" value={t.betrag} onChange={(e) => setTeile(teile.map((x, k) => (k === i ? { ...x, betrag: e.target.value } : x)))} />
              {/* Modell B: ohne Vorgabe teilt die App Hauskosten nach Fläche; eine Vorgabe gilt für diesen Teil ganz */}
              <select className={`${feld} w-36`} aria-label="Nutzung" value={t.nutzung ?? ""}
                onChange={(e) => setTeile(teile.map((x, k) => (k === i ? { ...x, nutzung: (e.target.value || null) as typeof x.nutzung } : x)))}>
                <option value="">nach Fläche</option><option value="vermietet">ganz vermietet</option>
                <option value="arbeitszimmer">ganz Arbeitszimmer</option><option value="privat">ganz privat</option>
              </select>
              <button type="button" aria-label="Teil entfernen" disabled={teile.length <= 1} className="text-ios-subtle disabled:opacity-30"
                onClick={() => setTeile(teile.filter((_, k) => k !== i))}><TrashIcon className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3 text-[13px]">
            <button type="button" className="text-ios-blue" onClick={() => setTeile([...teile, { steuerArtId: "", betrag: euro(ziel - summe), nutzung: null }])}>+ Teil</button>
            <span className={summe === ziel ? "text-ios-green" : "text-ios-blue"}>Summe {euro(summe)} €{summe === ziel ? " ✓" : ` — es fehlen ${euro(ziel - summe)} €`}</span>
            <button type="button" disabled={summe !== ziel || speichern.isPending} onClick={() => speichern.mutate()}
              className="ml-auto px-3 py-1 rounded-ios bg-ios-blue text-white disabled:opacity-40">Aufteilung speichern</button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-[13px] text-ios-muted">Steuerjahr</label>
        <input className={`${feld} w-24`} inputMode="numeric" value={jahr} onChange={(e) => setJahr(e.target.value)} />
        {!b.steuerjahr_bestaetigt && <span className="text-[12px] text-ios-blue">Vorschlag aus dem Buchungsdatum</span>}
        {b.jahreswechsel && <span className="text-[12px] text-ios-blue">Jahreswechsel (§ 11): regelmäßig wiederkehrende Zahlung? Dann zählt das Jahr, zu dem sie gehört.</span>}
        <button type="button" disabled={jahrSpeichern.isPending} onClick={() => jahrSpeichern.mutate()} className="px-3 py-1 rounded-ios bg-white shadow-card text-ios-blue">Steuerjahr speichern</button>
        <span className="ml-auto" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-[13px] text-ios-muted">Leistungszeitraum</label>
        <input type="date" aria-label="Leistungszeitraum von" className={feld} value={lz.von} onChange={(e) => setLz({ ...lz, von: e.target.value })} />
        <span>–</span>
        <input type="date" aria-label="Leistungszeitraum bis" className={feld} value={lz.bis} onChange={(e) => setLz({ ...lz, bis: e.target.value })} />
        <button type="button" disabled={lzSpeichern.isPending} onClick={() => lzSpeichern.mutate()} className="px-3 py-1 rounded-ios bg-white shadow-card text-ios-blue">speichern</button>
        <span className="text-[12px] text-ios-muted">leer = Monat der Buchung; bei Jahresabrechnung/Nachzahlung den abgerechneten Zeitraum (Fläche dieses Zeitraums, Steuerjahr = Zahlung)</span>
        <span className="ml-auto" />
        {b.bestaetigt_am === null || b.status === "geaendert"
          ? <button type="button" disabled={bestaetigen.isPending} onClick={() => bestaetigen.mutate()} className="px-3 py-1 rounded-ios bg-ios-blue text-white">Bestätigen</button>
          : <button type="button" disabled={aufheben.isPending} onClick={() => aufheben.mutate()} className="px-3 py-1 rounded-ios bg-white shadow-card text-ios-dim">Bestätigung aufheben</button>}
      </div>
      {protokoll && protokoll.length > 0 && (
        <details className="text-[12px] text-ios-muted">
          <summary className="cursor-pointer">Änderungen ({protokoll.length})</summary>
          {protokoll.map((p, i) => <div key={i}>{formatDateTime(new Date(p.zeit * 1000).toISOString())} · {p.username ?? "System"} · {p.feld}: {p.alt ?? "—"} → {p.neu ?? "—"}</div>)}
        </details>
      )}
    </div>
  );
}

const artName = (arten: Buchungsart[], id: string) => arten.find((a) => a.id === id)?.name ?? "?";
